import { encryptPayload, decryptPayload } from '~/composables/useEncryptedAPI'

interface Options {
  headers?: Record<string, any>
  params?: Record<string, any>
  query?: Record<string, any>
  body?: any
  auth?: boolean
  client?: boolean
}

interface Response<T = any> {
  data: T | null
  error: { message: string } | null
}

/** Convert FormData to plain object */
function formDataToObject(fd: FormData): Record<string, any> {
  const obj: Record<string, any> = {}
  fd.forEach((value, key) => {
    obj[key] = value
  })
  return obj
}

export const useGCAPIHandler = async <T = any>(
  method: string,
  url: string,
  options: Options = {}
): Promise<Response<T>> => {
  try {
    // Prepare body (handle FormData)
    let body: any = options.body ?? {}
    if (body instanceof FormData) body = formDataToObject(body)
    else if (body?.__type === 'form-data') {
      body = { ...body }
      delete body.__type
    }

    // Build payload to encrypt
    const payloadToEncrypt = {
      path: url,
      method,
      headers: options.headers ?? {},
      params: options.params ?? {},
      query: options.query ?? {},
      body,
      auth: options.auth ?? null,
      client: options.client ?? null
    }

    // Encrypt payload
    const encrypted = await encryptPayload(payloadToEncrypt)

    // Send encrypted request
    const apiResult: any = await $giftCardServer(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: {
        secret: encrypted.secret,
        data: encrypted.data
      }
    })

    // Decrypt server response if encrypted
    if (apiResult?.data?.data) {
      const decrypted = decryptPayload(
        encrypted._clientAesRawBase64,
        encrypted._clientIvBase64,
        apiResult.data.data
      )

      return { data: decrypted ?? null, error: null }
    }

    // Fallback if response is not encrypted
    return { data: apiResult?.data ?? null, error: apiResult?.error ?? { message: 'Unknown error' } }

  } catch (err: any) {
    return { data: null, error: { message: err?.message ?? 'Network error' } }
  }
}
